// Phase 122 — deciding whether a picked file is usable exam audio.
//
// The Phase 120 hook rejected anything outside a six-entry MIME allow-list,
// which is stricter than reality: browsers disagree about audio types (Safari
// reports `.m4a` as audio/x-m4a, some report audio/mp3 instead of audio/mpeg)
// and a file dragged from certain filesystems arrives with an EMPTY `type`.
// Those rejections read to the teacher as "the upload did nothing".
//
// So: trust an `audio/*` type when the browser gives one, otherwise fall back
// to the extension — the same lenient rule the import-audio-url Edge Function
// already applies to remote files. Storage is the real gate (private bucket,
// staff-only INSERT, bucket-level size limit); this check only exists to give
// a fast, specific error instead of an opaque one.

/** Extensions we accept, mapped to the type we send as `contentType`. Mirrors
 *  AUDIO_EXTENSIONS in supabase/functions/import-audio-url/index.ts. */
const AUDIO_EXTENSIONS = new Map<string, string>([
  ['mp3', 'audio/mpeg'],
  ['m4a', 'audio/mp4'],
  ['mp4', 'audio/mp4'],
  ['aac', 'audio/aac'],
  ['wav', 'audio/wav'],
  ['ogg', 'audio/ogg'],
  ['oga', 'audio/ogg'],
  ['webm', 'audio/webm'],
  ['flac', 'audio/flac'],
]);

/** The `accept` attribute for every listening-audio file input. Kept beside
 *  the extension map so the two can never drift apart. */
export const AUDIO_ACCEPT_ATTR = 'audio/*,.mp3,.m4a,.mp4,.aac,.wav,.ogg,.oga,.webm,.flac';

export function audioExtensionOf(fileName: string): string {
  const match = /\.([a-z0-9]{2,5})$/i.exec(fileName.trim());
  return (match?.[1] ?? '').toLowerCase();
}

/**
 * The content type to store this file under, or null when it does not look
 * like audio at all. An `audio/*` browser type wins; otherwise the extension
 * decides. `video/mp4` is accepted because an audio-only MP4 is routinely
 * labelled that way and plays fine in an `<audio>` element.
 */
export function resolveAudioType(file: { name: string; type: string }): string | null {
  const declared = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
  if (declared.startsWith('audio/')) return declared;
  const byExtension = AUDIO_EXTENSIONS.get(audioExtensionOf(file.name));
  if (byExtension !== undefined) return byExtension;
  if (declared === 'video/mp4') return 'audio/mp4';
  return null;
}

/** Human-readable size, for error messages ("File is 118.4 MB; max is 100 MB"). */
export function formatMegabytes(bytes: number): string {
  return (bytes / 1024 / 1024).toFixed(1);
}
